import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { getStrictCurrentEnterpriseId } from '@mews-commander/core';
import { GeneralArea, GeneralSignInMethod, useRepositoryMutation } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import {
    checkForForced2FA,
    getDeviceFingerprintPayload,
    INITIAL_SIGN_IN_PARAMS,
    initializeUserSession,
} from '../../ducks/actions';

export function useSignIn() {
    const navigate = useNavigate();
    const dispatch = useCommanderThunkDispatch();
    const [isLoginPending, setIsLoginPending] = useState(false);
    // This is not great, because despite saying signInApiWithToken you need to pass the correct method
    // This is due a limitation of our useRepositoryMutation hook
    // TODO: Propose a default arguments object for mutations for ease of use
    // https://mews.atlassian.net/browse/OPS-22700
    const { mutateAsync: signInApiWithToken } = useRepositoryMutation('generalUsers', 'signIn', {
        meta: {
            disableDefaultErrorHandler: true,
        },
    });
    const { mutateAsync: signInApi } = useRepositoryMutation('generalUsers', 'signIn', {
        onError: async () => navigate({ to: '/Home/SignIn' }),
    });

    const signIn = useCallback(async () => {
        // If the user has logged in via other means we need to login via access token.
        // Our current way of detecting this is simply asking to the BE,
        // since all of the tokens are HTTP-only and cannot be accessed from the client.
        const commonLoginPayload = {
            ...INITIAL_SIGN_IN_PARAMS,
            device: getDeviceFingerprintPayload(),
            application: GeneralArea.Commander,
        };
        setIsLoginPending(true);
        return (
            signInApiWithToken({
                ...commonLoginPayload,
                method: GeneralSignInMethod.AccessToken,
            })
                .catch(async () =>
                    signInApi({
                        ...commonLoginPayload,
                        method: GeneralSignInMethod.SignInToken,
                    })
                )
                .then(async (data) => dispatch(checkForForced2FA(data)))
                .then(async (data) => dispatch(initializeUserSession(data)))
                .then(async () => {
                    await navigate({
                        to: '/$currentEnterpriseId/Dashboard/Index',
                        params: {
                            currentEnterpriseId: getStrictCurrentEnterpriseId(),
                        },
                    });
                })
                // eslint-disable-next-line @mews/no-empty-catch
                .catch(() => {
                    // Mute errors from promise rejections in dispatch calls.
                    // Mutation errors are handled in the mutation itself.
                })
                .finally(() => setIsLoginPending(false))
        );
    }, [dispatch, navigate, signInApi, signInApiWithToken]);

    return {
        signIn,
        isLoginPending,
    };
}
